"use client"

import { type FormEvent, useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Card, Form, Button, Spinner, Toast } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { LanguageSwitcher } from "../../components/language-switcher"
import { PasswordInput } from "../../components/ui/password-input"
import { useAuth } from "../../contexts/auth-context"
import { NativeMessaging } from "../../utils/native-messaging"

export default function LoginPage() {
  const { login, isLoading, error, clearError } = useAuth()
  const { t } = useTranslation()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success")
  const navigate = useNavigate()

  // Handle login token from native app
  const handleLoginToken = async (token: string) => {
    const [email, password] = token.split("|")

    try {
      await login(email, password, false)
      setToastVariant("success")
      setToastMessage("Login successful! Welcome back to UnitySmartLiving!")
      setShowToast(true)
      window.location.href = "/dashboard" // Redirect to dashboard
    } catch (error: any) {
      setToastVariant("danger")
      setToastMessage(error.message || "Invalid email or password")
      setShowToast(true)
    }
  }

  useEffect(() => {
    // Send native message when LoginPage loads
    NativeMessaging.sendMessage("authentication")

    // Attach handleLoginToken to window for native access
    ;(window as any).handleLoginToken = handleLoginToken

    // Cleanup function to remove the global function when component unmounts
    return () => {
      delete (window as any).handleLoginToken
    }
  }, [])

  useEffect(() => {
    if (error) {
      setToastVariant("danger")
      setToastMessage(error)
      setShowToast(true)
      clearError()
    }
  }, [error, clearError])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      await login(email, password, true)
      setToastVariant("success")
      setToastMessage("Login successful! Welcome back to UnitySmartLiving!")
      setShowToast(true)
      navigate("/dashboard")
    } catch (error: any) {
      // Error handling is done in the auth context and useEffect above
    }
  }

  return (
    <div className="gradient-page-centered p-3">
      <div className="position-absolute top-0 end-0 p-3">
        <LanguageSwitcher />
      </div>

      <Card
        className="w-100 border-0 shadow-lg"
        style={{
          maxWidth: "420px",
          borderRadius: "16px",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <h3 className="fw-bold mb-1" style={{ color: "#101e46" }}>
              {t("login")}
            </h3>
          </div>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-medium" style={{ color: "#2f3b5e" }}>
                {t("emailAddress")}
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-0 shadow-sm"
                style={{
                  borderRadius: "12px",
                  padding: "12px 16px",
                  fontSize: "16px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid rgba(60, 130, 245, 0.2)",
                }}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label htmlFor="login-password" className="fw-medium mb-0" style={{ color: "#2f3b5e" }}>
                  {t("password")}
                </Form.Label>
                <Link to="/auth/forgot-password" className="small text-decoration-none" style={{ color: "#3c82f5" }}>
                  {t("forgotPassword")}
                </Link>
              </div>
              <PasswordInput
                id="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Button type="submit" className="w-100 fw-medium shadow-sm mb-3 btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {t("loading")}
                </>
              ) : (
                t("loginButton")
              )}
            </Button>
          </Form>

          <div className="mt-4 text-center">
            <span style={{ color: "#64748b", fontSize: "14px" }}>{t("noAccount")} </span>
            <Link to="/auth/register" className="text-decoration-none fw-medium" style={{ color: "#3c82f5" }}>
              {t("createAccount")}
            </Link>
          </div>
        </Card.Body>
      </Card>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={3000}
        autohide
        className="position-fixed bottom-0 end-0 m-3"
        bg={toastVariant}
      >
        <Toast.Header>
          <strong className="me-auto">{toastVariant === "success" ? "Success" : "Error"}</strong>
        </Toast.Header>
        <Toast.Body className={toastVariant === "danger" ? "text-white" : undefined}>{toastMessage}</Toast.Body>
      </Toast>
    </div>
  )
}
